import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { BehaviorSubject, catchError, concatMap, defer, exhaustMap, filter, finalize, groupBy, map, mergeMap, Observable, of, switchMap, take, takeUntil, tap, timer, withLatestFrom } from 'rxjs';
import { Action, Store } from '@ngrx/store';
import { AppState } from '../root';
import { AudioDeviceActions } from './audio-device.actions';
import { AudioDeviceService } from '../../services/audio-device/audio-device.service';
import { WebRtcAudioService } from '../../services/webrtc-audio/webrtc-audio.service';
import { ToastService } from '../../services/toast/toast.service';
import { ToastType } from '../../models/toast.model';
import { IAudioDevice } from '../../models/audio-device.interface';
import { ILoadableArray } from '../../models/loadable';
import { AuditActions } from '../audit/audit.actions';
import { AlertActions } from '../alert/alert.actions';
import { selectLoggedInUser } from '../user/user.selectors';
import { UserActions } from '../user/user.actions';
import { selectAudioDevicesBySite } from './audio-device.selectors';
import { initialSiteDevicesState } from './audio-device.state';
import { selectWindowAlerts } from '../alert/alert.selectors';

@Injectable()
export class AudioDeviceEffects {
  private actions$ = inject(Actions);
  private store$ = inject(Store<AppState>);
  private audioDeviceService = inject(AudioDeviceService);
  private webRtcAudioService = inject(WebRtcAudioService);
  private toastService = inject(ToastService);
  private audioLoadRetryDelayMs = 3000;
  private audioLoadRetryUntil = new Map<string, number>();
  private audioLoadLastError = new Map<string, Error>();
  private audioSendRetryDelayMs = 1500;
  private audioSendMaxRetries = 2;
  private firstSendsInFlight$ = new BehaviorSubject(0);

  loadBySite$ = createEffect(() => this.actions$.pipe(
    ofType(AudioDeviceActions.loadBySite),
    groupBy((action) => action.siteId),
    mergeMap((site$) => site$.pipe(
      exhaustMap((action) => this.store$.select(selectAudioDevicesBySite).pipe(
        take(1),
        map((devicesBySite) => devicesBySite[action.siteId] ?? initialSiteDevicesState()),
        mergeMap((siteDevices) => this.resolveLoadBySite$(action, siteDevices))
      ))
    ))
  ));

  private resolveLoadBySite$(
    action: { siteId: string; force?: boolean },
    siteDevices: ILoadableArray<IAudioDevice>,
  ): Observable<Action> {
    if (!action.force && siteDevices.success) {
      return of(AudioDeviceActions.loadBySiteSuccess({
        siteId: action.siteId,
        devices: siteDevices.items,
      }));
    }

    const retryUntil = this.audioLoadRetryUntil.get(action.siteId) ?? 0;
    const lastError = this.audioLoadLastError.get(action.siteId);
    if (Date.now() < retryUntil && lastError) {
      return of(AudioDeviceActions.loadBySiteFailure({
        siteId: action.siteId,
        error: lastError,
      }));
    }

    return this.audioDeviceService.getBysite(action.siteId).pipe(
      map((devices) => {
        this.audioLoadRetryUntil.delete(action.siteId);
        this.audioLoadLastError.delete(action.siteId);
        return AudioDeviceActions.loadBySiteSuccess({ siteId: action.siteId, devices });
      }),
      catchError((error) => {
        this.audioLoadRetryUntil.set(action.siteId, Date.now() + this.audioLoadRetryDelayMs);
        this.audioLoadLastError.set(action.siteId, error);
        return of(AudioDeviceActions.loadBySiteFailure({ siteId: action.siteId, error }));
      })
    );
  }

  clearRetryStateOnLogout$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.logout),
    map(() => {
      this.audioLoadRetryUntil.clear();
      this.audioLoadLastError.clear();
      return AudioDeviceActions.clearRetryState();
    })
  ));

  createDevice$ = createEffect(() => this.actions$.pipe(
    ofType(AudioDeviceActions.createDevice),
    switchMap(({ device }) =>
      this.audioDeviceService.create([device]).pipe(
        map((devices) => AudioDeviceActions.createDeviceSuccess({ siteId: device.site_id, devices })),
        catchError((error) => of(AudioDeviceActions.createDeviceFailure({ error })))
      )
    )
  ));

  updateDevice$ = createEffect(() => this.actions$.pipe(
    ofType(AudioDeviceActions.updateDevice),
    switchMap(({ device }) =>
      this.audioDeviceService.update(device).pipe(
        map((updated) => AudioDeviceActions.updateDeviceSuccess({ device: updated })),
        catchError((error) => of(AudioDeviceActions.updateDeviceFailure({ error })))
      )
    )
  ));

  deleteDevice$ = createEffect(() => this.actions$.pipe(
    ofType(AudioDeviceActions.deleteDevice),
    switchMap(({ siteId, id }) =>
      this.audioDeviceService.delete([id]).pipe(
        map(() => AudioDeviceActions.deleteDeviceSuccess({ siteId, id })),
        catchError((error) => of(AudioDeviceActions.deleteDeviceFailure({ siteId, error })))
      )
    )
  ));

  // First attempts run in parallel; failures fall back to one-at-a-time retries because
  // several speakers can share one NVR that only allows a single audio backchannel.
  // Retries wait for in-flight first attempts so they don't collide
  sendAudio$ = createEffect(() => this.actions$.pipe(
    ofType(AudioDeviceActions.sendAudio),
    mergeMap((request) => defer(() => {
      this.firstSendsInFlight$.next(this.firstSendsInFlight$.value + 1);
      return this.attemptSendAudio$({ ...request, attempt: 0 });
    }).pipe(
      finalize(() => this.firstSendsInFlight$.next(this.firstSendsInFlight$.value - 1))
    ))
  ));

  retrySendAudio$ = createEffect(() => this.actions$.pipe(
    ofType(AudioDeviceActions.retrySendAudio),
    concatMap((request) => this.firstSendsInFlight$.pipe(
      filter((count) => count === 0),
      take(1),
      concatMap(() => timer(this.audioSendRetryDelayMs)),
      concatMap(() => this.attemptSendAudio$(request))
    ))
  ));

  private attemptSendAudio$(request: {
    deviceId: string;
    audioUrl: string;
    sensorName: string;
    alertIds?: string[];
    attempt: number;
  }): Observable<Action> {
    const { deviceId, audioUrl, sensorName, alertIds, attempt } = request;
    return this.webRtcAudioService.sendAudio(deviceId, audioUrl).pipe(
      takeUntil(this.actions$.pipe(ofType(AudioDeviceActions.stopAudio))),
      map(() => {
        this.toastService.showToast(`Audio sent successfully to ${sensorName}`, ToastType.SUCCESS);
        return AudioDeviceActions.sendAudioSuccess({ deviceId, sensorName, alertIds });
      }),
      catchError((error) => {
        console.warn(`Send audio to ${sensorName} failed (attempt ${attempt + 1})`, error);
        if (attempt < this.audioSendMaxRetries) {
          return of(AudioDeviceActions.retrySendAudio({ deviceId, audioUrl, sensorName, alertIds, attempt: attempt + 1 }));
        }
        this.toastService.showToast(`Failed to send audio to ${sensorName}`, ToastType.ERROR);
        return of(AudioDeviceActions.sendAudioFailure({ error }));
      })
    );
  }

  sendAudioSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(AudioDeviceActions.sendAudioSuccess),
    withLatestFrom(this.store$.select(selectLoggedInUser)),
    map(([{ deviceId, sensorName }, user]) => AuditActions.addAuditEntry({
      auditRequest: {
        action: 'SENT',
        user_id: user.id,
        organisation_id: user.organisation_id,
        item_id: deviceId,
        table_name: 'AUDIO_DEVICE',
        message: `SENT Audio to Audio Device - ${sensorName}`,
      }
    }))
  ));

  commentOnAlertAfterAudioSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(AudioDeviceActions.sendAudioSuccess),
    filter(({ alertIds }) => !!alertIds && alertIds.length > 0),
    withLatestFrom(this.store$.select(selectWindowAlerts)),
    map(([{ sensorName, alertIds }, windowAlerts]) => {
      const alerts = windowAlerts.filter(a => alertIds!.includes(a.id));
      return AlertActions.commentOnAlerts({
        alerts,
        comment: `Audio sent to ${sensorName}`,
      });
    }),
    filter(action => action.alerts.length > 0)
  ));

  stopAudio$ = createEffect(() => this.actions$.pipe(
    ofType(AudioDeviceActions.stopAudio),
    tap(() => this.webRtcAudioService.stopAudio())
  ), { dispatch: false });
}
